import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

const inputVariants = cva(
  "flex w-full rounded-lg border bg-white transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        // Default keeps the original h-9 / text-base-on-mobile sizing — no breakage.
        sm: "h-8 px-2.5 py-1 text-sm",
        default:
          "h-9 px-3 py-1 text-base md:text-sm",
        lg: "h-10 px-3.5 py-2 text-base md:text-sm",
      },
      state: {
        default:
          "border-input hover:border-muted-foreground/40 focus-visible:border-primary",
        error:
          "border-destructive/60 text-destructive placeholder:text-destructive/50 focus-visible:ring-destructive/40 focus-visible:border-destructive",
      },
    },
    defaultVariants: {
      size: "default",
      state: "default",
    },
  }
)

type NativeInputProps = Omit<React.ComponentProps<"input">, "size">

export interface InputProps extends NativeInputProps, VariantProps<typeof inputVariants> {
  /** Element rendered before the input text. Receives container padding adjustment. */
  startIcon?: React.ReactNode
  /** Element rendered after the input text. */
  endIcon?: React.ReactNode
  /** Show a clear (×) button at the end when the input has a value. */
  onClear?: () => void
  /** Convenience: error helper text rendered below the input. */
  errorText?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size,
      state,
      startIcon,
      endIcon,
      onClear,
      errorText,
      value,
      ...props
    },
    ref
  ) => {
    const effectiveState = state ?? (errorText || props['aria-invalid'] ? "error" : "default")
    const hasStart = !!startIcon
    const showClear = !!onClear && value !== undefined && value !== null && value !== ""
    const hasEnd = !!endIcon || showClear

    // Padding adjustments for icon slots.
    const paddingClass = cn(
      hasStart && "ps-9",
      hasEnd && "pe-9"
    )

    const inputEl = (
      <input
        type={type}
        ref={ref}
        value={value}
        className={cn(
          inputVariants({ size, state: effectiveState }),
          paddingClass,
          className
        )}
        aria-invalid={effectiveState === "error" || undefined}
        {...props}
      />
    )

    if (!hasStart && !hasEnd) {
      return errorText ? (
        <div className="space-y-1">
          {inputEl}
          <p className="text-[0.8rem] font-medium text-destructive">{errorText}</p>
        </div>
      ) : (
        inputEl
      )
    }

    return (
      <div className={errorText ? "space-y-1" : undefined}>
        <div className="relative">
          {hasStart && (
            <span className="pointer-events-none absolute start-0 top-0 inline-flex h-full w-9 items-center justify-center text-muted-foreground [&_svg]:size-4">
              {startIcon}
            </span>
          )}
          {inputEl}
          {hasEnd && (
            <span className="absolute end-0 top-0 inline-flex h-full items-center justify-center pe-1 text-muted-foreground">
              {showClear ? (
                <button
                  type="button"
                  onClick={onClear}
                  aria-label="Clear"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : (
                <span className="inline-flex h-full w-9 items-center justify-center [&_svg]:size-4">
                  {endIcon}
                </span>
              )}
            </span>
          )}
        </div>
        {errorText && (
          <p className="text-[0.8rem] font-medium text-destructive">{errorText}</p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
